import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useUser } from './UserContext';

/* ========= API base (ENV -> fallback) ========= */
const ENV_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';
const API_BASE = (ENV_BASE || '').replace(/\/+$/, '') || "https://www.doroosonline.co.il";
const DEFAULT_TIMEOUT_MS = 15000;
const url = (path) => `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;

/* ========= tiny fetch helper (same spirit as UserContext) ========= */
async function parseJSONSafely(res) {
  if (res.status === 204) return {};
  const txt = await res.text();
  if (!txt) return {};
  try {
    return JSON.parse(txt);
  } catch {
    return { raw: txt };
  }
}

async function toJSON(res) {
  const data = await parseJSONSafely(res);
  if (!res.ok || (data && data.ok === false)) {
    const msg =
      data?.error ||
      data?.message ||
      (res.status === 401 ? 'שגיאה בהרשאות (401)' : `שגיאת שרת (${res.status})`);
    const err = new Error(msg);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data ?? {};
}

async function apiFetch(path, { method = 'GET', body, token, timeout = DEFAULT_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await fetch(url(path), {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      signal: controller.signal,
    });

    return await toJSON(res);
  } catch (e) {
    if (e.name === 'AbortError') {
      throw new Error('זמן ההמתנה לשרת הסתיים. נסה שוב בעוד רגע.');
    }
    if (e instanceof TypeError) {
      const hint = API_BASE
        ? `בדוק שהשרת זמין ב-${API_BASE} וש-CORS מוגדר נכון.`
        : 'הגדר VITE_API_BASE/REACT_APP_API_BASE כדי לפנות לשרת הנכון.';
      throw new Error(`שגיאת רשת: לא ניתן להגיע לשרת. ${hint}`);
    }
    throw e;
  } finally {
    clearTimeout(t);
  }
}

/* ========= constants ========= */
const AvailabilityContext = createContext(null);

export const defaultDaysOfWeek = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'שבת'];
export const defaultTimeSlots = [
  { start: '8:00', end: '8:50' },   { start: '9:00', end: '9:50' },
  { start: '10:00', end: '10:50' }, { start: '11:00', end: '11:50' },
  { start: '12:00', end: '12:50' }, { start: '13:00', end: '13:50' },
  { start: '14:00', end: '14:50' }, { start: '15:00', end: '15:50' },
  { start: '16:00', end: '16:50' }, { start: '17:00', end: '17:50' },
  { start: '18:00', end: '18:50' }, { start: '19:00', end: '19:50' },
];

const makeGrid = (days, times, fill = false) =>
  Array.from({ length: days }, () => Array.from({ length: times }, () => !!fill));

const initialAvailability =
  makeGrid(defaultDaysOfWeek.length, defaultTimeSlots.length, false);

const gridsEqual = (a, b) => {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].length !== b[i].length) return false;
    for (let j = 0; j < a[i].length; j++) {
      if (!!a[i][j] !== !!b[i][j]) return false;
    }
  }
  return true;
};

const normalizeMonthlyAvailability = (raw) => {
  if (!raw || typeof raw !== 'object') return {};

  const source =
    raw.availability ??
    raw.items ??
    raw.slots ??
    raw.data ??
    raw;

  if (!source || typeof source !== 'object') return {};

  const out = {};

  for (const [dateKey, value] of Object.entries(source)) {
    if (!Array.isArray(value)) continue;

    out[dateKey] = value
      .map((v) => String(v || '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }

  return out;
};

export const AvailabilityProvider = ({ children }) => {
  const { user, showAlert } = useUser();

  const [availability, setAvailability] = useState(initialAvailability);
  const [savedAvailability, setSavedAvailability] = useState(initialAvailability);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const daysCount = defaultDaysOfWeek.length;
  const timesCount = defaultTimeSlots.length;

  // read JWT (same keys as in UserContext)
  const token = useMemo(() => {
    try {
      const keys = ['token', 'jwt', 'JWT', 'accessToken', 'authToken'];
      for (const k of keys) {
        const v = localStorage.getItem(k) || sessionStorage.getItem(k);
        if (v) return v;
      }
    } catch {
      // ignore
    }
    return null;
  }, [user?.id]);

  /* ===== load weekly base availability grid from server ===== */
  const reload = useCallback(async () => {
    if (!user || !token) return;

    setIsLoading(true);
    try {
      const data = await apiFetch(
        `/api/availability/me?shape=grid&days=${daysCount}&times=${timesCount}`,
        {
          method: 'GET',
          token,
        }
      );

      const grid = Array.isArray(data?.grid) ? data.grid : initialAvailability;

      const normalized = makeGrid(daysCount, timesCount, false);
      for (let d = 0; d < Math.min(daysCount, grid.length); d++) {
        for (let t = 0; t < Math.min(timesCount, (grid[d] || []).length); t++) {
          normalized[d][t] = !!grid[d][t];
        }
      }

      setAvailability(normalized);
      setSavedAvailability(normalized);
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בטעינת זמינות', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [user, token, daysCount, timesCount, showAlert]);

  useEffect(() => {
    reload();
  }, [reload]);

  /* ===== fetch real monthly availability map from backend =====
     returns:
     {
       "2026-03-29": ["16:00", "17:00", ...],
       "2026-03-30": ["08:00", "09:00", ...]
     }
     This endpoint already applies blocking / released-slot rules on the server.
  */
  const getMonthlyAvailability = useCallback(
    async (userId, year, month) => {
      if (!userId) {
        throw new Error('Missing userId for monthly availability');
      }

      const y = Number(year);
      const m = Number(month);

      if (!Number.isInteger(y) || y < 1970) {
        throw new Error('Invalid year for monthly availability');
      }
      if (!Number.isInteger(m) || m < 1 || m > 12) {
        throw new Error('Invalid month for monthly availability');
      }

      const data = await apiFetch(
        `/api/availability/${encodeURIComponent(userId)}/month?year=${y}&month=${m}`,
        {
          method: 'GET',
          token,
        }
      );

      return normalizeMonthlyAvailability(data);
    },
    [token]
  );

  /* ===== local toggles ===== */
  const toggleCell = useCallback((dayIndex, timeIndex) => {
    setAvailability((prev) => {
      const next = prev.map((r) => r.slice());
      next[dayIndex][timeIndex] = !next[dayIndex][timeIndex];
      return next;
    });
  }, []);

  const toggleDay = useCallback((dayIndex) => {
    setAvailability((prev) => {
      const isFull = prev[dayIndex].every(Boolean);
      const next = prev.map((row, idx) =>
        idx === dayIndex ? row.map(() => !isFull) : row.slice()
      );
      return next;
    });
  }, []);

  const toggleTime = useCallback((timeIndex) => {
    setAvailability((prev) => {
      const isFull = prev.every((day) => !!day[timeIndex]);
      return prev.map((row) =>
        row.map((v, idx) => (idx === timeIndex ? !isFull : v))
      );
    });
  }, []);

  const toggleAll = useCallback(() => {
    setAvailability((prev) => {
      const allOn = prev.flat().every(Boolean);
      return prev.map((row) => row.map(() => !allOn));
    });
  }, []);

  /* ===== save & delete (server) ===== */
  const saveAvailability = useCallback(async () => {
    if (!user || !token) return;

    setIsSaving(true);
    try {
      await apiFetch('/api/availability/me', {
        method: 'PUT',
        token,
        body: {
          shape: 'grid',
          days: daysCount,
          times: timesCount,
          grid: availability,
        },
      });

      setSavedAvailability(availability);
      showAlert?.('הזמינות נשמרה בהצלחה', 'success');
    } catch (err) {
      showAlert?.(err.message || 'שגיאה בשמירת זמינות', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [user, token, availability, daysCount, timesCount, showAlert]);

  const deleteAvailability = useCallback(async () => {
    if (!user || !token) return;

    setIsSaving(true);
    try {
      await apiFetch('/api/availability/me', { method: 'DELETE', token });
      const empty = makeGrid(daysCount, timesCount, false);
      setAvailability(empty);
      setSavedAvailability(empty);
      showAlert?.('הזמינות אופסה', 'success');
    } catch (err) {
      showAlert?.(err.message || 'שגיאה באיפוס זמינות', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [user, token, daysCount, timesCount, showAlert]);

  const dirty = useMemo(
    () => !gridsEqual(availability, savedAvailability),
    [availability, savedAvailability]
  );

  const value = useMemo(
    () => ({
      daysOfWeek: defaultDaysOfWeek,
      timeSlots: defaultTimeSlots,
      availability,
      savedAvailability,
      isLoading,
      isSaving,
      dirty,
      toggleCell,
      toggleDay,
      toggleTime,
      toggleAll,
      saveAvailability,
      deleteAvailability,
      reload,
      getMonthlyAvailability,
    }),
    [
      availability,
      savedAvailability,
      isLoading,
      isSaving,
      dirty,
      toggleCell,
      toggleDay,
      toggleTime,
      toggleAll,
      saveAvailability,
      deleteAvailability,
      reload,
      getMonthlyAvailability,
    ]
  );

  if (typeof window !== 'undefined' && !ENV_BASE) {
    console.warn(`[AvailabilityContext] API base not set; using fallback ${API_BASE}`);
  }

  return (
    <AvailabilityContext.Provider value={value}>
      {children}
    </AvailabilityContext.Provider>
  );
};

export const useAvailability = () => {
  const ctx = useContext(AvailabilityContext);
  if (!ctx) throw new Error('useAvailability must be used within an AvailabilityProvider');
  return ctx;
};